import React, { useState, useEffect } from 'react';
import {
  User,
  Lock,
  Bell,
  Palette,
  Globe,
  Shield,
  Save,
  Moon,
  Sun,
  Monitor,
  Loader2,
  CheckCircle,
  AlertCircle,
  Settings,
  TrendingUp,
  EyeOff
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from '../../hooks/use-toast';

import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { settingsAPI, usersAPI } from '../../services/api';
import { cn } from '../../lib/utils';

const SettingsPage = () => {
  const { user } = useAuth();
  const { hideIncentivesForEmployees, updateIncentiveVisibility, isAdmin } = usePrivacy();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAdmin, setSavingAdmin] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });
  const [theme, setTheme] = useState('system');
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [hideIncentives, setHideIncentives] = useState(false);
  const [notifications, setNotifications] = useState({
    email_new_lead: true,
    email_demo_reminder: true,
    email_deal_updates: true,
    inapp_new_lead: true,
    inapp_demo_reminder: true,
    inapp_deal_updates: true
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsAPI.getProfile();
        if (res.data?.preferences) {
          setNotifications(prev => ({ ...prev, ...res.data.preferences }));
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  // Sync local state with context
  useEffect(() => {
    setHideIncentives(hideIncentivesForEmployees);
  }, [hideIncentivesForEmployees]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateProfile({ theme, language, timezone });
      toast({ title: 'Settings saved' });
    } catch (error) {
      console.error('Failed to save:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to save settings',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordMessage({ type: '', text: '' });
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'All fields are required' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    setChangingPassword(true);
    try {
      await usersAPI.changePassword({
        current_password: currentPassword,
        new_password: newPassword
      });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      setPasswordMessage({ type: 'error', text: errorMsg });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await settingsAPI.updateProfile({ notifications });
      toast({ title: 'Notification settings saved' });
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdminSettings = async () => {
    setSavingAdmin(true);
    try {
      const result = await updateIncentiveVisibility(hideIncentives);
      if (result.success) {
        toast({ title: 'Admin settings saved successfully' });
      } else {
        toast({
          variant: 'destructive',
          title: 'Failed to save admin settings',
          description: result.error
        });
      }
    } catch (error) {
      console.error('Failed to save admin settings:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to save admin settings',
        description: error.message
      });
    } finally {
      setSavingAdmin(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-sm text-slate-500">Manage your account preferences</p>
      </div>

      <Tabs defaultValue="profile" className="space-y-4 sm:space-y-6">
        <TabsList className="bg-slate-100 w-full flex overflow-x-auto">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4">
            <User className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="appearance" className="data-[state=active]:bg-white flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4">
            <Palette className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Appearance</span>
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4">
            <Bell className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Notifications</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-white flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4">
            <Shield className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="admin" className="data-[state=active]:bg-white flex-1 sm:flex-none text-xs sm:text-sm px-2 sm:px-4">
              <Settings className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Admin</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card className="bg-white border-slate-200">
            <CardHeader className="px-4 sm:px-6">
              <CardTitle className="text-base sm:text-lg">Profile Information</CardTitle>
              <CardDescription className="text-sm">Update your personal details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-slate-700 flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                  {user?.avatar || user?.name?.split(' ').map(n => n[0]).join('')}
                </div>
                <Button variant="outline" size="sm">Change Avatar</Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Full Name</Label>
                  <Input defaultValue={user?.name} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Email</Label>
                  <Input defaultValue={user?.email} disabled />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Role</Label>
                  <Input defaultValue={user?.role?.replace('_', ' ')} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                      <SelectItem value="America/New_York">Eastern (ET)</SelectItem>
                      <SelectItem value="Europe/London">London (GMT)</SelectItem>
                      <SelectItem value="Asia/Singapore">Singapore (SGT)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-[200px]">
                    <Globe className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="hi">Hindi</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button className="bg-slate-800 hover:bg-slate-900" onClick={handleSaveProfile}>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Appearance</CardTitle>
              <CardDescription>Customize how the app looks</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label>Theme</Label>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => setTheme('light')}
                    className={cn(
                      'p-4 rounded-lg border-2 transition-colors flex flex-col items-center gap-2',
                      theme === 'light' ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <Sun className="w-6 h-6" />
                    <span className="text-sm font-medium">Light</span>
                  </button>
                  <button
                    onClick={() => setTheme('dark')}
                    className={cn(
                      'p-4 rounded-lg border-2 transition-colors flex flex-col items-center gap-2',
                      theme === 'dark' ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <Moon className="w-6 h-6" />
                    <span className="text-sm font-medium">Dark</span>
                  </button>
                  <button
                    onClick={() => setTheme('system')}
                    className={cn(
                      'p-4 rounded-lg border-2 transition-colors flex flex-col items-center gap-2',
                      theme === 'system' ? 'border-slate-800 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
                    )}
                  >
                    <Monitor className="w-6 h-6" />
                    <span className="text-sm font-medium">System</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Choose how you want to be notified</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-slate-800">Email Notifications</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">New Lead Assigned</p>
                      <p className="text-sm text-slate-500">Get notified when a new lead is assigned to you</p>
                    </div>
                    <Switch 
                      checked={notifications.email_new_lead}
                      onCheckedChange={(v) => setNotifications({...notifications, email_new_lead: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Demo Reminders</p>
                      <p className="text-sm text-slate-500">Receive reminders before scheduled demos</p>
                    </div>
                    <Switch 
                      checked={notifications.email_demo_reminder}
                      onCheckedChange={(v) => setNotifications({...notifications, email_demo_reminder: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Deal Updates</p>
                      <p className="text-sm text-slate-500">Get notified on deal stage changes</p>
                    </div>
                    <Switch 
                      checked={notifications.email_deal_updates}
                      onCheckedChange={(v) => setNotifications({...notifications, email_deal_updates: v})}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium text-slate-800">In-App Notifications</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">New Lead Assigned</p>
                      <p className="text-sm text-slate-500">Show in-app notification for new leads</p>
                    </div>
                    <Switch 
                      checked={notifications.inapp_new_lead}
                      onCheckedChange={(v) => setNotifications({...notifications, inapp_new_lead: v})}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-700">Demo Reminders</p>
                      <p className="text-sm text-slate-500">Show reminders in the app</p>
                    </div>
                    <Switch 
                      checked={notifications.inapp_demo_reminder}
                      onCheckedChange={(v) => setNotifications({...notifications, inapp_demo_reminder: v})}
                    />
                  </div>
                </div>
              </div>

              <Button className="bg-slate-800 hover:bg-slate-900" onClick={handleSaveNotifications}>
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your password and sessions</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-medium text-slate-800">Change Password</h3>
                
                {/* Password change message */}
                {passwordMessage.text && (
                  <div className={cn(
                    "flex items-center gap-2 p-3 rounded-lg",
                    passwordMessage.type === 'success' ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
                  )}>
                    {passwordMessage.type === 'success' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <AlertCircle className="w-4 h-4" />
                    )}
                    <span className="text-sm">{passwordMessage.text}</span>
                  </div>
                )}
                
                <div className="space-y-3 max-w-md">
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input 
                      type="password" 
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter your current password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>New Password</Label>
                    <Input 
                      type="password" 
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Confirm New Password</Label>
                    <Input 
                      type="password" 
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>
                  <Button 
                    className="bg-slate-800 hover:bg-slate-900"
                    onClick={handleChangePassword}
                    disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  >
                    {changingPassword ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Changing...
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4 mr-2" />
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h3 className="font-medium text-slate-800 mb-4">Active Sessions</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">Current Session</p>
                      <p className="text-sm text-slate-500">Chrome on Windows • Mumbai, India</p>
                    </div>
                    <span className="text-xs text-emerald-600 font-medium">Active Now</span>
                  </div>
                </div>
                <Button variant="destructive" className="mt-4">
                  Logout from all devices
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin Tab - Admin Only */}
        {isAdmin && (
          <TabsContent value="admin">
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Admin Settings
                </CardTitle>
                <CardDescription>Configure system-wide settings for all users</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Incentive Visibility Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="w-5 h-5 text-amber-600" />
                    <h3 className="font-medium text-slate-800">Incentive Visibility</h3>
                  </div>
                  
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <p className="font-medium text-amber-800">Hide Incentives for Employees</p>
                        <p className="text-sm text-amber-700">
                          When enabled, employees will not be able to see the Incentives menu or any incentive-related information anywhere in the application. Only admins will have access.
                        </p>
                      </div>
                      <Switch 
                        checked={hideIncentives}
                        onCheckedChange={setHideIncentives}
                        className="data-[state=checked]:bg-amber-500"
                      />
                    </div>
                    
                    {hideIncentives && (
                      <div className="mt-3 p-3 bg-amber-100 rounded-lg flex items-center gap-2">
                        <EyeOff className="w-4 h-4 text-amber-600" />
                        <span className="text-sm text-amber-700">
                          Incentives are currently hidden from all employees
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <Button 
                  className="bg-slate-800 hover:bg-slate-900"
                  onClick={handleSaveAdminSettings}
                  disabled={savingAdmin}
                >
                  {savingAdmin ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Admin Settings
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
